// Лист управления песней — нижний лист владельца (волна F, задача 6).
//
// Что это. Владелец песни один раз в месяц хочет ответить себе на четыре
// вопроса: кто её видит, кто открывал, какая запись главная, как снять с
// публикации. До волны F ответы были рассыпаны по строке: кнопка читателей
// стояла третьим слотом, а видимость пряталась внутри открывшейся панели.
// Прототип (docs/prototypes/2026-09-18-online-library.html, `openOwnerSheet`)
// собирает их в одно место и кладёт НИЖНИМ ЛИСТОМ поверх списка: лист не
// раздвигает строки, то есть под пальцем ничего не уезжает, и на телефоне
// приезжает снизу — туда, где палец и так стоит.
//
// Здесь — только КАРКАС: заголовок, подпись владельца и пустое место под
// разделы (`handle.body`). Разделы кладут задачи 7 и 8; этот модуль о них не
// знает и знать не должен.
//
// Модуль чистый: ни localStorage, ни i18n — подписи приходят параметрами,
// ровно как в sort-control.ts и song-row-controls.ts. Перевод и данные
// остаются у library-view.ts.
//
// Почему лист монтируется на <body>, а не внутрь строки. layout.css даёт
// `.app > *` `position: relative; z-index: 1`, то есть КОНТЕКСТ НАЛОЖЕНИЯ:
// внутри него любой z-index прижат к уровню 1 и липкая шапка приложения
// (z-index 20) накрыла бы затемнение. Host выбирает вызывающий — тесты
// подставляют свой, приложение передаёт document.body.

export interface SongSheetLabels {
  /** Имя песни — видимый заголовок листа. */
  title: string;
  /** Доступное имя листа целиком: лист накрывает строку, из которой пришёл,
   *  поэтому одного имени песни мало — нужно сказать, ЧТО это за поверхность. */
  dialogLabel: string;
  /** «You published this song» — почему эти органы вообще показаны. */
  ownerNote: string;
  /** Подпись (она же доступное имя) кнопки закрытия. */
  close: string;
}

export interface SongSheetOpts {
  labels: SongSheetLabels;
  /** Куда монтировать лист и затемнение. */
  host: HTMLElement;
  /** Кому вернуть фокус после закрытия — кнопка «⋯», из которой лист открыли.
   *  Без этого фокус после закрытия падает на <body>, и клавиатура (а с ней и
   *  скринридер) теряет строку, ради которой лист открывали. */
  returnFocusTo?: HTMLElement | null;
  /** Зовётся РОВНО ОДИН раз, после того как лист снят. */
  onClose?: () => void;
}

export interface SongSheetHandle {
  /** Сам лист (role="dialog"). */
  sheet: HTMLElement;
  /** Затемнение под ним. */
  scrim: HTMLElement;
  /** Пустое место под разделы — сюда кладут задачи 7 и 8. */
  body: HTMLElement;
  /** Идемпотентно: второй вызов ничего не делает и onClose не повторяет. */
  close: () => void;
}

/** Открытых листов не бывает двух. */
let openHandle: SongSheetHandle | null = null;

/**
 * Что считается органом, до которого доходит Tab. Скрытые (`hidden`),
 * выключенные и вынутые из обхода (`tabindex="-1"`) не считаются — ровно то
 * же множество, по которому ходит сам браузер.
 *
 * Раскладки здесь не спрашивают намеренно: happy-dom её не считает, а живой
 * лист прячет органы атрибутами и `disabled`, а не нулевой шириной. Замок
 * ловит то, что есть в разметке; глазами это проверено на телефоне.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusablesIn(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)]
    .filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}

export function openSongSheet(opts: SongSheetOpts): SongSheetHandle {
  openHandle?.close();

  const scrim = document.createElement('div');
  scrim.dataset.role = 'song-sheet-scrim';
  scrim.className = 'lib-scrim';

  const sheet = document.createElement('div');
  sheet.dataset.role = 'song-sheet';
  sheet.className = 'lib-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', opts.labels.dialogLabel);

  // Полоска-ручка: на телефоне она и есть признак «это лист, его можно
  // смахнуть вниз». На широком экране прячется (лист там — карточка по центру).
  const grip = document.createElement('div');
  grip.className = 'lib-sheet-grip';
  grip.setAttribute('aria-hidden', 'true');
  sheet.appendChild(grip);

  const head = document.createElement('div');
  head.className = 'lib-sheet-head';

  const heads = document.createElement('div');
  heads.className = 'lib-sheet-heads';
  const title = document.createElement('p');
  title.dataset.role = 'song-sheet-title';
  title.className = 'lib-sheet-title';
  title.textContent = opts.labels.title;
  const sub = document.createElement('p');
  sub.dataset.role = 'song-sheet-sub';
  sub.className = 'lib-sheet-sub';
  sub.textContent = opts.labels.ownerNote;
  heads.appendChild(title);
  heads.appendChild(sub);
  head.appendChild(heads);

  // Закрытие названо словом, а не только жестом: смахнуть лист вниз и попасть
  // по затемнению — это мышь и палец, а Escape — клавиатура. Кнопка закрывает
  // дыру между ними (и остаётся единственным способом там, где затемнение
  // перекрыто, например под экранной лупой).
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.dataset.role = 'song-sheet-close';
  closeBtn.className = 'lib-sheet-close';
  closeBtn.textContent = opts.labels.close;
  head.appendChild(closeBtn);
  sheet.appendChild(head);

  const body = document.createElement('div');
  body.dataset.role = 'song-sheet-body';
  body.className = 'lib-sheet-body';
  sheet.appendChild(body);

  let closed = false;

  /**
   * Tab ЗАПЕРТ ВНУТРИ ЛИСТА, и это следствие `aria-modal="true"` выше, а не
   * украшение. Атрибут говорит скринридеру, что всё вне диалога не
   * существует; выпусти Tab наружу — и пользователь AT получает фокус на «⋯»
   * соседней строки, то есть на органе, который его читалка считает скрытым.
   * Курсор чтения и фокус тогда расходятся, и человек управляет тем, чего для
   * него нет.
   *
   * Развилка была честная: либо запереть Tab, либо снять `aria-modal` и
   * признать поверхность немодальной. Заперт — потому что лист накрывает
   * экран затемнением и модален по поведению, а не только по атрибуту.
   *
   * Перехватываются РОВНО три случая — уход за последний орган, уход перед
   * первым и Tab из-за пределов листа (фокус мог оказаться там после
   * перерисовки, уронившей нажатый узел). Обычный ход по середине списка
   * остаётся браузеру: собственный обход повторял бы его порядок и разошёлся
   * бы с ним на первом же новом органе.
   */
  const onTab = (e: KeyboardEvent): void => {
    const items = focusablesIn(sheet);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !sheet.contains(active)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); return; }
    if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') onTab(e);
  };

  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    scrim.remove();
    sheet.remove();
    if (openHandle === handle) openHandle = null;
    // Фокус возвращается ДО onClose: вызывающий может в нём перерисовать
    // список, и тогда возвращать было бы уже некуда.
    opts.returnFocusTo?.focus();
    opts.onClose?.();
  }

  scrim.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', onKey);

  opts.host.appendChild(scrim);
  opts.host.appendChild(sheet);

  // Фокус уходит внутрь листа: иначе он остаётся на «⋯» ПОД затемнением, и
  // клавиатура ходит по списку, которого не видно.
  closeBtn.focus();

  const handle: SongSheetHandle = { sheet, scrim, body, close };
  openHandle = handle;
  return handle;
}
